import { useServices } from "../../lib/queries";
import { relativeTime } from "../../lib/format";

const STATUS_COLOR: Record<string, string> = {
  healthy: "var(--green)",
  warning: "var(--amber)",
  at_risk: "var(--red)",
  unknown: "var(--text-quiet)",
};

export default function ServiceDetailDrawer({ id }: { id: string }) {
  const { data: services = [] } = useServices();
  const s = services.find((x) => x.id === id);
  if (!s) return <div className="px-4 py-3 text-xs text-text-quiet">service not found</div>;

  return (
    <div className="flex flex-col gap-3 p-4">
      <div className="flex items-center justify-between">
        <div>
          <span
            className="font-data text-micro"
            style={{ letterSpacing: "0.13em", color: "var(--text-quiet)" }}
          >
            SERVICE
          </span>
          <h2 className="mt-1 text-base font-semibold text-text">{s.name}</h2>
        </div>
        <span
          className="font-data text-micro inline-flex items-center gap-1 rounded-sm border px-2 py-0.5"
          style={{
            borderColor: STATUS_COLOR[s.status] ?? "var(--border-strong)",
            color: STATUS_COLOR[s.status] ?? "var(--text-quiet)",
          }}
        >
          <span
            className="inline-block rounded-full"
            style={{
              width: 6,
              height: 6,
              backgroundColor: STATUS_COLOR[s.status] ?? "var(--text-quiet)",
            }}
          />
          {s.status}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <Field label="Category" value={s.category ?? "—"} />
        <Field label="Plan" value={s.plan ?? "—"} />
        <Field label="Login" value={s.login_method ?? "—"} />
        <Field label="Email" value={s.account_email ?? "—"} />
        <Field
          label="Cost"
          value={(s.monthly_cost_usd ?? 0) > 0 ? `$${s.monthly_cost_usd}/mo` : "free"}
        />
        <Field label="Renews" value={s.renews_at ? relativeTime(`${s.renews_at}T00:00:00Z`) : "—"} />
        <Field label="Checked" value={s.last_checked_at ? relativeTime(s.last_checked_at) : "never"} />
        <Field label="Used by" value={(s.used_by ?? []).length ? `${(s.used_by ?? []).length} projects` : "—"} />
      </div>
      {(s.used_by ?? []).length > 0 ? (
        <div className="rounded border border-border bg-surface p-2">
          <span
            className="font-data text-micro"
            style={{ letterSpacing: "0.1em", color: "var(--text-quiet)" }}
          >
            USED BY
          </span>
          <div className="mt-1 flex flex-wrap gap-1">
            {(s.used_by ?? []).map((u) => (
              <span
                key={u}
                className="font-data text-micro rounded-sm border border-border-strong bg-surface-2 px-1.5 py-0.5 text-text-dim"
              >
                {u}
              </span>
            ))}
          </div>
        </div>
      ) : null}
      {s.notes ? (
        <p className="rounded border border-border bg-surface p-2 text-xs text-text-dim">{s.notes}</p>
      ) : null}
      {s.dashboard_url ? (
        <a
          href={s.dashboard_url}
          target="_blank"
          rel="noopener noreferrer"
          className="font-data text-xs text-accent-strong"
          style={{ color: "var(--accent-strong)" }}
        >
          Open dashboard ↗
        </a>
      ) : null}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col rounded border border-border bg-surface px-2 py-1.5">
      <span
        className="font-data text-micro"
        style={{ letterSpacing: "0.1em", color: "var(--text-quiet)" }}
      >
        {label.toUpperCase()}
      </span>
      <span className="truncate text-xs text-text">{value}</span>
    </div>
  );
}
